import { Avatar, Box, Button, CircularProgress, Container, Grid2 as Grid, Stack, Typography, Select, MenuItem, InputLabel } from "@mui/material"
import { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { toastError } from "../../../redux/guest-slices/other.slice"
import { updateAdminProfileReq, updateAdminPasswordReq } from "../../../redux/admin-slices/auth.slice"
import InputFieldA from "../../../components/ui/InputFieldA"
import Icons from "../../../assets/js/Icons"

function Settings() {
  const dispatch = useDispatch()
  const { user, other } = useSelector(state => state.authSlice)

  const [gender, setGender] = useState("")
  const [imageFile, setImageFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState("")

  const fileInputRef = useRef(null)

  useEffect(() => {
    if (user?.gender) {
      setGender(user.gender)
    }
  }, [user])

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setImageFile(file)
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  const handleProfileSubmit = (e) => {
    e.preventDefault()
    const form = e.target
    const data = Object.fromEntries(new FormData(form))

    if (!data.fullName || data.fullName.trim() === "") {
      return dispatch(toastError("Name is required!"))
    }

    const formData = new FormData()
    formData.append("fullName", data.fullName)
    formData.append("phone", data.phone)
    formData.append("gender", gender)
    if (imageFile) {
      formData.append("image", imageFile)
    }

    dispatch(updateAdminProfileReq(formData))
  }

  const handlePasswordSubmit = (e) => {
    e.preventDefault()
    const form = e.target
    const data = Object.fromEntries(new FormData(form))

    if (!data.currentPassword || !data.password || !data.confPassword) {
      return dispatch(toastError("Please fill in all password fields!"))
    }

    if (data.password !== data.confPassword) {
      return dispatch(toastError("New password and confirm password do not match!"))
    }

    dispatch(updateAdminPasswordReq(data))
      .unwrap()
      .then(() => {
        form.reset()
      })
      .catch(() => {})
  }

  return (
    <Container maxWidth={false} fixed sx={{ py: 5 }}>
      <Box sx={{ mb: 5 }}>
        <Typography
          sx={{
            fontSize: "32px",
            lineHeight: "1.2",
            fontWeight: "500"
          }}
          variant="pageTitle"
        >Account Settings
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column: Personal Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              p: 3,
              width: "100%",
              overflow: "hidden",
              borderRadius: "12px",
              bgcolor: "white",
              border: "1px solid #00000012",
            }}
            component="form"
            onSubmit={handleProfileSubmit}
          >
            <Typography variant="h5" fontWeight="600" mb={3} sx={{ fontFamily: "Poppins" }}>
              Personal Information
            </Typography>

            <Stack spacing={3}>
              {/* Profile Image Upload */}
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    position: "relative",
                    width: 100,
                    height: 100,
                    mx: "auto",
                    mb: 1,
                    cursor: "pointer",
                    borderRadius: "50%",
                    overflow: "hidden",
                    "&:hover .overlay": { opacity: 1 }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Avatar
                    src={previewUrl || user?.image}
                    sx={{ width: 100, height: 100 }}
                  />
                  <Box
                    className="overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: "rgba(0,0,0,0.5)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      opacity: 0,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    <Icons.UploadSolid width={24} height={24} fill="white" />
                  </Box>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Click avatar to upload photo
                </Typography>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleImageChange}
                />
              </Box>

              {/* Name */}
              <Box>
                <InputFieldA
                  key={`name-${user?._id}-${user?.fullName}`}
                  type="text"
                  name="fullName"
                  label="Full Name"
                  value={user?.fullName || ""}
                  placeholder="Enter your name"
                />
              </Box>

              {/* Email (Read Only) */}
              <Box>
                <InputFieldA
                  key={`email-${user?._id}`}
                  type="email"
                  label="Email Address"
                  value={user?.email || ""}
                  disabled
                  placeholder="Email address"
                />
              </Box>

              {/* Phone */}
              <Box>
                <InputFieldA
                  key={`phone-${user?._id}-${user?.phone}`}
                  type="text"
                  name="phone"
                  label="Phone Number"
                  value={user?.phone || ""}
                  placeholder="Enter phone number"
                />
              </Box>

              {/* Gender */}
              <Box>
                <InputLabel sx={{ mb: 1, fontFamily: "Poppins" }}>Gender</InputLabel>
                <Select
                  fullWidth
                  size="small"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>Select Gender</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="others">Others</MenuItem>
                </Select>
              </Box>

              <Box sx={{ textAlign: "end", mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ minWidth: "150px" }}
                  endIcon={
                    other?.loadingProfile && (
                      <CircularProgress size={22} color="inherit" thickness={6} />
                    )
                  }
                  disabled={other?.loadingProfile}
                >
                  Save Profile
                </Button>
              </Box>
            </Stack>
          </Box>
        </Grid>

        {/* Right Column: Change Password */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              p: 3,
              width: "100%",
              overflow: "hidden",
              borderRadius: "12px",
              bgcolor: "white",
              border: "1px solid #00000012",
            }}
            component="form"
            onSubmit={handlePasswordSubmit}
          >
            <Typography variant="h5" fontWeight="600" mb={3} sx={{ fontFamily: "Poppins" }}>
              Change Password
            </Typography>

            <Stack spacing={3}>
              <Box>
                <InputFieldA
                  type="password"
                  name="currentPassword"
                  label="Current Password"
                  placeholder="Enter current password"
                />
              </Box>

              <Box>
                <InputFieldA
                  type="password"
                  name="password"
                  label="New Password"
                  placeholder="Enter new password"
                />
              </Box>

              <Box>
                <InputFieldA
                  type="password"
                  name="confPassword"
                  label="Confirm New Password"
                  placeholder="Confirm new password"
                />
              </Box>

              <Box sx={{ textAlign: "end", mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ minWidth: "150px" }}
                  endIcon={
                    other?.loadingPassword && (
                      <CircularProgress size={22} color="inherit" thickness={6} />
                    )
                  }
                  disabled={other?.loadingPassword}
                >
                  Update Password
                </Button>
              </Box>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Container>
  )
}

export default Settings
